/*
,------.                                    ,--.          ,--.      ,--------.    ,------.            ,--.           
|  .-.  \,--. ,--.,--,--,  ,--,--.,--,--,--.`--' ,---.    |  | ,---.'--.  .--'    |  .-.  \  ,--,--.,-'  '-. ,--,--. 
|  |  \  :\  '  / |      \' ,-.  ||        |,--.| .--'    |  || .-. |  |  |       |  |  \  :' ,-.  |'-.  .-'' ,-.  | 
|  '--'  / \   '  |  ||  |\ '-'  ||  |  |  ||  |\ `--.    |  |' '-' '  |  |       |  '--'  /\ '-'  |  |  |  \ '-'  | 
`-------'.-'  /   `--''--' `--`--'`--`--`--'`--' `---'    `--' `---'   `--'       `-------'  `--`--'  `--'   `--`--' 
         `---'                                                                                                      
*/
import { SVGIcons } from '../../map/svg';
import { TimeSelect } from '../../components/timeselect';
import { TrackLine } from '../../map/polyline';
import { AssetService, InputType } from '../../service/asset';
import { AssetTelemetry } from '../../service/latest';
import { PaginatedFetcher } from '../../service/paginatedFetcher';
import { AssetTelemetryService, DeviceTelemetryDTO } from '../../service/telemetry';
import { VisionWindowManager } from '../../window/manager';
import { VisionWindow } from '../../window/window';
import { createLabeledMarker } from '../../map/helpers';
import { start } from 'repl';

interface TelemetryResponse {
    data: DeviceTelemetryDTO[];
    lastDateOccurred: string | null;
}

/** Stores the history data for an asset.
 * Has the track line and speed data and track grid.
 *
 */
class AssetData {
    trackGrid!: dhtmlXGridObject;
    pointsToolbar!: dhtmlXToolbarObject;
    telemetryData!: DeviceTelemetryDTO[];
    polyline!: TrackLine;

    speeddata = new google.visualization.DataTable();
    speedview!: google.visualization.DataView;

    rowData: { id: string; data: (string | number | boolean)[] }[] = [];
    parent: TrackingWindow;
    id: string;
    colour: string;

    idleStartTime: Date | undefined;
    idleStartLat: number;
    idleStartLon: number;
    idle: boolean;
    assetName: string;
    stopMarkers!: google.maps.marker.AdvancedMarkerElement[];
    userDefinedStopTimeSeconds: number;

    constructor(parent: TrackingWindow, id: string, userDefinedStopTimeSeconds: number) {
        this.parent = parent;
        this.id = id;
        this.idleStartTime = undefined;
        this.idleStartLat = 0;
        this.idleStartLon = 0;
        this.idle = false;
        this.stopMarkers = new Array<google.maps.marker.AdvancedMarkerElement>();
        this.colour = parent.assetGrid.cellById(id, 2).getValue();
        this.assetName = parent.assetGrid.cellById(id, 1).getValue();
        this.userDefinedStopTimeSeconds = userDefinedStopTimeSeconds;

        this.pointsToolbar = parent.tabbar.cells('data_points').attachToolbar();
        this.pointsToolbar.addButton('export_xls', 1, 'Excel', 'icons/excel.png', '');
        this.pointsToolbar.addButton('export_pdf', 2, 'PDF', 'icons/pdf.png', '');

        this.trackGrid = parent.tabbar.cells('data_points').attachGrid();

        this.trackGrid.setHeader('Time,Ign,Speed(m/hr),Lat,Lng,I1,I2,I3,I4', '', [
            'text-align:left;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
            'text-align:center;',
        ]);

        this.trackGrid.setColTypes('ro,ro,ro,ro,ro,ro,ro,ro,ro');
        this.trackGrid.setColAlign('left,center,center,center,center,center,center,center,center');
        this.trackGrid.setImagesPath('css/imgs/');
        this.trackGrid.enableMultiselect(false);
        this.trackGrid.init();
        this.polyline = new TrackLine();

        this.polyline.setColour(parent.assetGrid.cellById(id, 2).getValue());
        this.polyline.setMap(parent.manager.app.layout.avl2Tab.mapPanel.map);

        this.speeddata = new google.visualization.DataTable();
        this.speeddata.addColumn('date', 'Time');
        this.speeddata.addColumn('number', 'Speed');
        this.speeddata.addColumn('string', 'id');

        // Details toolbar handlers to export the currently displayed trips in either PDF or Excel format.
        this.pointsToolbar.attachEvent('onClick', (id) => {
            switch (id) {
                case 'export_xls': {
                    this.getReport('xlsx');
                    break;
                }
                case 'export_pdf': {
                    this.getReport('pdf');
                    break;
                }
            }
        });
    }

    private getReport(format: 'pdf' | 'xlsx') {
        const startTime = this.parent.paramForm.getItemValue(TimeSelect.START).getTime();
        const endTime = this.parent.paramForm.getItemValue(TimeSelect.END).getTime();

        if (format == 'pdf') {
            this.parent.manager.app.tripService.downloadPDF('/tracking/report?asset=' + this.id + '&start=' + startTime + '&end=' + endTime + '&format=pdf', `trip_report_${Date.now()}.${format}`);
        } else if (format == 'xlsx') {
            this.parent.manager.app.tripService.downloadXLSX('/tracking/report?asset=' + this.id + '&start=' + startTime + '&end=' + endTime + '&format=xlsx', `trip_report_${Date.now()}.${format}`);
        }
    }

    /** Load the telemetry
     *
     * @param telemetry The data from the server to load in for this asset.
     */
    public loadTelemetry(telemetry: DeviceTelemetryDTO[]): void {
        telemetry.forEach((dataPoint) => {
            // Don't use data if 0/0 lat,lon
            if (dataPoint.lat == 0 && dataPoint.lon == 0) {
                return 0;
            }

            this.speeddata.addRow([new Date(dataPoint.timestamp), this.parent.manager.app.km2miles(dataPoint.speed), dataPoint.id]);

            // Data to push into the data grid.
            const elementData = new Array<string | number | boolean>();
            const utcDate = dataPoint.timestamp;
            elementData.push(
                new Date(utcDate).toLocaleDateString('en-us', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    second: 'numeric',
                })
            );
            const speed = this.parent.manager.app.km2miles(dataPoint.speed);
            if (dataPoint.ignition == true) {
                elementData.push('ON');
            } else if (dataPoint.ignition == false) {
                elementData.push('OFF');
            } else {
                elementData.push('-');
            }
            elementData.push(speed.toFixed(0));
            elementData.push(dataPoint.lat.toFixed(4));
            elementData.push(dataPoint.lon.toFixed(4));

            const act1 = this.parent.assetService.getActivityForInput(this.id, 1, dataPoint.input1);
            elementData.push(act1 == undefined ? '' : act1);
            const act2 = this.parent.assetService.getActivityForInput(this.id, 2, dataPoint.input2);
            elementData.push(act2 == undefined ? '' : act2);
            const act3 = this.parent.assetService.getActivityForInput(this.id, 3, dataPoint.input3);
            elementData.push(act3 == undefined ? '' : act3);
            const act4 = this.parent.assetService.getActivityForInput(this.id, 4, dataPoint.input4);
            elementData.push(act4 == undefined ? '' : act4);

            this.rowData.push({ id: dataPoint.id, data: elementData });

            //this.trackGrid.addRow(dataPoint.id, elementData, 0, 0, '', false);

            this.trackGrid.setUserData(dataPoint.id, 'location', new google.maps.LatLng(dataPoint.lat, dataPoint.lon));

            // Add a marker point to build up the polyline
            const marker = this.polyline.addPoint(dataPoint.lat, dataPoint.lon, '#000000', dataPoint.heading);
            if (dataPoint.speed < 1) {
                if (this.idle == false) {
                    console.log('Potential Idle Started at:');
                    console.log(new Date(dataPoint.timestamp));
                    this.idle = true;
                    this.idleStartTime = new Date(dataPoint.timestamp);
                    this.idleStartLat = dataPoint.lat;
                    this.idleStartLon = dataPoint.lon;
                }
            } else if (dataPoint.speed >= 1) {
                const addresscontent = 'N/A';
                if (this.idle == true) {
                    if (this.idleStartTime != undefined) {
                        console.log('Checking:');
                        console.log(new Date(dataPoint.timestamp).getTime() - this.idleStartTime.getTime());

                        if (new Date(dataPoint.timestamp).getTime() - this.idleStartTime.getTime() > this.userDefinedStopTimeSeconds * 1000) {
                            console.log('Triggered');
                            /* this.userDefinedStopTimeSeconds * 1000) {*/
                            const idleTime = (new Date(dataPoint.timestamp).getTime() - this.idleStartTime.getTime()) / 60000;
                            const start = new Date(this.idleStartTime.getTime()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            const stop = new Date(dataPoint.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                            const contentHtml = `
                                <div>
                                    <div style="flex:1;">
                                        <div style="font-size:12px; color:#666;">${this.assetName} - ${idleTime.toFixed(0)} minutes</div>
                                        <div class="addresscontent" style="font-size:14px; color:#333; font-weight:600;">${addresscontent}</div>
                                    </div>
                                    <div style="display:flex; justify-content:space-between; font-size:12px; color:#666; margin-top:6px;">
                                        <div>
                                            Start: <span style="color:#333; font-weight:600;">${start}</span>
                                            <br>
                                            End: <span style="color:#333; font-weight:600;">${stop}</span>
                                        </div>
                                    </div>
                                </div>
                                `;

                            this.idle = false;
                            const marker = createLabeledMarker(
                                this.parent.manager.app.layout.avl2Tab.mapPanel.map.map,
                                { lat: dataPoint.lat, lng: dataPoint.lon },
                                SVGIcons.stopSign('red'),
                                contentHtml
                            );

                            this.stopMarkers.push(marker);
                        } else {
                            this.idle = false;
                        }
                    }
                }
            }

            // When user clicks the marker, highlight and zoom.
            marker.addListener('click', () => {
                this.parent.selectAssetFromGrid(this.id);
                this.trackGrid.selectRowById(dataPoint.id, true, true, false);
                const location = this?.trackGrid.getUserData(dataPoint.id, 'location');
                this.parent.manager.app.layout.avl2Tab.mapPanel.zoomToLocation(location);
            });

            const infoWindow = new google.maps.InfoWindow({
                content: "Hello, I'm a popup!",
                pixelOffset: new google.maps.Size(8, -32),
            });

            marker.addListener('mouseover', () => {
                infoWindow.setPosition(marker.getPosition());
                infoWindow.open(this.parent.manager.app.layout.avl2Tab.mapPanel.map);

                const asset = this.parent.manager.app.assetService.assetList.get(this.id);
                const item = {} as AssetTelemetry;
                item.input1 = dataPoint.input1;
                item.input2 = dataPoint.input2;
                item.input3 = dataPoint.input3;
                item.input4 = dataPoint.input4;

                let spreaderStatus = undefined;
                let plowStatus = undefined;
                let gradingStatus = undefined;
                if (asset) {
                    spreaderStatus = AssetService.getActivityStatus(InputType.SPREADER, asset.config, item);
                    plowStatus = AssetService.getActivityStatus(InputType.PLOW, asset.config, item);
                    gradingStatus = AssetService.getActivityStatus(InputType.MOLDBOARD, asset.config, item);
                }

                let content =
                    ` <TABLE>
                    <TR><TD>Date</TD><TD>` +
                    elementData[0] +
                    `</TD></TR>
                    <TR><TD>Speed</TD><TD>` +
                    elementData[2] +
                    `</TD></TR>`;
                if (spreaderStatus != undefined) {
                    content += `<TR><TD>Spreader</TD><TD>` + (spreaderStatus == true ? 'ON' : 'OFF') + `</TD></TR>`;
                }
                if (plowStatus != undefined) {
                    content += `<TR><TD>Plow</TD><TD>` + (plowStatus == true ? 'DOWN' : 'UP') + `</TD></TR>`;
                }
                if (gradingStatus != undefined) {
                    content += `<TR><TD>Grading</TD><TD>` + (gradingStatus == true ? 'DOWN' : 'UP') + `</TD></TR>`;
                }
                content += `</TABLE>`;

                infoWindow.setContent(content);
            });

            // Hide popup when mouse leaves
            marker.addListener('mouseout', () => {
                console.log('Mover OUT');
                infoWindow.close();
            });

            // When user clicks in grid, locate the point on the map.
            this.trackGrid.attachEvent('onRowSelect', (rId, _ind) => {
                const location = this.trackGrid.getUserData(rId, 'location');
                this.parent.manager.app.layout.avl2Tab.mapPanel.zoomToLocation(location);
                this.parent.manager.app.layout.avl2Tab.mapPanel.tempCircle(location as google.maps.LatLng);
            });
        });

        this.trackGrid.parse({ rows: this.rowData }, 'json');
        this.trackGrid.adjustColumnSize(0);
        this.polyline.showMarkers(false);
        this.polyline.setMap(this.parent.manager.app.layout.avl2Tab.mapPanel.map);

        //this.stopMarkers.forEach((marker) => {
        /*
                            this.parent.manager.app.telemetryService.getAddress(this.idleStartLat, this.idleStartLon).then((data) => {
                                if (data.parsedBody) {
                                    const address = data.parsedBody;
                                    const addresscontent = address.housenumber + ' ' + address.addressStreet;

                                    marker._ui.markerContent.querySelector('addresscontent').textContent = addresscontent;
                                }
                            });
                            */

        //});
        //this.drawSpeedGraph();
        this.updateMarkers();
    }

    async updateMarkers() {
        const promises = this.stopMarkers.map(async (marker) => {
            const pos = marker.position;
            if (pos?.lat == undefined || pos?.lng == undefined) return;
            const lat = typeof pos.lat === 'function' ? pos.lat() : pos.lat;
            const lng = typeof pos.lng === 'function' ? pos.lng() : pos.lng;
            const data = await this.parent.manager.app.telemetryService.getAddress(lat, lng);

            if (!data?.parsedBody) return;

            const address = data.parsedBody;
            const addresscontent = address.housenumber + ' ' + address.addressStreet;

            if (marker.content instanceof HTMLElement) {
                const addressElement = marker.content.querySelector('.addresscontent');
                if (addressElement) {
                    addressElement.textContent = addresscontent;
                }
            }
        });

        await Promise.all(promises);
    }

    /** Draws the google chart for the speed.
     *
     */
    public drawSpeedGraph(): void {
        this.parent.speedgraph.clearChart();
        this.speedview = new google.visualization.DataView(this.speeddata);
        this.speedview.setColumns([0, 1]); //here you set the columns you want to display

        this.parent.speedgraph.draw(this.speedview, this.parent.speedoptions);

        google.visualization.events.removeAllListeners(this.parent.speedgraph);

        google.visualization.events.addListener(this.parent.speedgraph, 'select', () => {
            const selection = this.parent.speedgraph.getSelection();
            if (selection.length > 0) {
                const row = selection[0].row;
                if (row) {
                    console.log(this.speeddata.getValue(row, 2));
                    const location = this.trackGrid.getUserData(this.speeddata.getValue(row, 2), 'location');
                    this.parent.manager.app.layout.avl2Tab.mapPanel.zoomToLocationAndLevel(location, 16);
                    this.parent.manager.app.layout.avl2Tab.mapPanel.tempCircle(location);
                }
            }
        });
    }

    /**
     *  Redraw the speed graph
     */
    redraw() {
        this.parent.speedgraph.draw(this.speedview, this.parent.speedoptions);
    }

    clearStopMarkers() {
        this.stopMarkers.forEach((marker) => {
            marker.map = null;
        });

        this.stopMarkers.length = 0;
    }
}

//******************************************************************************************************************************** */

/** The window for handling the history track.
 *
 */
export class TrackingWindow extends VisionWindow {
    layout!: dhtmlXLayoutObject;
    assetGrid!: dhtmlXGridObject;
    paramForm!: dhtmlXForm;
    assetService: AssetService;
    telemetryService: AssetTelemetryService;

    tabbar!: dhtmlXTabBar;
    assetData!: Map<string, AssetData>;
    toolbar!: dhtmlXToolbarObject;
    pointsToolbar!: dhtmlXToolbarObject;

    speedgraph!: google.visualization.LineChart;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    speedoptions: any;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    statusbar!: any;

    userDefinedStopTimeSeconds = 300;

    constructor(manager: VisionWindowManager) {
        const height = window.innerHeight * 0.75;
        const width = window.innerWidth * 0.45;
        super(manager, {
            id: 'tracking_window',
            title: 'Tracking',
            x: 30,
            y: 30,
            width: width,
            height: height,
        });
        this.window.setText('Tracking');

        this.speedoptions = {
            hAxis: {
                title: 'Time',
            },
            vAxis: {
                title: 'Speed',
            },
            explorer: {
                //actions: ['dragToZoom', 'rightClickToReset'],
                axis: 'horizontal',
                maxZoomIn: 0.0001,
            },
        };

        this.manager.win.window('tracking_window').attachEvent('onClose', () => {
            this.visible = false;
            this.manager.app.layout.avl2Tab.trackingWindow = undefined;
            this.removeAllTracks();
            return true;
        });

        this.assetService = this.manager.app.assetService;
        this.telemetryService = this.manager.app.telemetryService;
        this.assetData = new Map<string, AssetData>();

        this.buildUI();
        this.buildEventHandlers();

        const graphElement = document.getElementById('tracking_speed_graph') as HTMLElement;
        this.speedgraph = new google.visualization.LineChart(graphElement);
    }

    /** Add an asset to the grid.
     * Will check to make sure that the asset is not already added.
     * @param id The id of the asset to add.
     */
    public addAsset(id: string): void {
        const asset = this.assetService.assetList.get(id);
        let defaultColour = '#00FF00';
        if (asset != null && asset.config.trackColour != undefined) {
            defaultColour = asset.config.trackColour;
        }

        if (asset != null) {
            if (this.assetGrid.getRowIndex(asset.id.id) == -1) {
                this.assetGrid.addRow(asset.id.id, [1, asset.id.name, defaultColour], 0, '', '', false);
            }
        }
    }

    /** Builds all the UI elements required
     *
     */
    private buildUI(): void {
        this.layout = this.window.attachLayout('3U');
        this.layout.cells('a').setText('Assets');
        this.layout.cells('b').setText('Time Selection');
        this.layout.cells('c').setText('Telemetry');

        this.layout.setSizes();
        this.assetGrid = this.layout.cells('a').attachGrid();
        this.assetGrid.setHeader('Display, Name, Color', '', []);
        this.assetGrid.setImagesPath('css/imgs/');
        this.assetGrid.setColTypes('ch,ro,cp');
        this.assetGrid.init();

        const formData = JSON.parse(JSON.stringify(TimeSelect.FORM));
        formData.push({
            type: 'input',
            name: 'stop_time',
            label: 'Stop Time (secs)',
            inputWidth: 50,
            labelWidth: '120',
            position: 'label-left',
            offsetLeft: '12',
            value: this.userDefinedStopTimeSeconds,
        });
        formData.push({ type: 'button', value: 'Search', name: 'search' });

        this.paramForm = this.layout.cells('b').attachForm(formData);
        this.paramForm.getInput(TimeSelect.START).autocomplete = 'off';

        this.toolbar = this.layout.attachToolbar();
        this.toolbar.addButtonTwoState('points', 4, 'Show Points', '', '');

        this.tabbar = this.layout.cells('c').attachTabbar();
        this.tabbar.addTab('data_points', 'Points', 100, 2, true, false);
        this.tabbar.addTab('speed', 'Speed', 100, 2, true, false);
        this.tabbar.cells('speed').attachHTMLString("<div id='tracking_speed_graph' style='width:100%;height:100%'></div>");
        this.tabbar.cells('data_points').setActive();

        this.statusbar = this.layout.cells('b').attachStatusBar();
        this.statusbar.setText('Loading Data');
        this.layout.cells('b').hideStatusBar();
    }

    /** Build up handlers required for the UI
     *
     */
    private buildEventHandlers(): void {
        // Form Events
        this.paramForm.attachEvent('onChange', (name, value, state) => {
            this.formChange(name, value, state);
        });

        this.paramForm.attachEvent('onButtonClick', (_id) => {
            this.getTracks();
        });

        // Grid Events
        this.assetGrid.attachEvent('onEditCell', (stage, rId, _cId, nValue, _oValue) => {
            this.assetGridEdit(stage, rId, nValue);
            return true;
        });

        this.assetGrid.attachEvent('onRowSelect', (id, ind) => {
            if (ind != 2) {
                this.selectAssetFromGrid(id);
            }
        });

        this.assetGrid.attachEvent('onCheck', (rId, cInd, state) => {
            if (cInd == 0) {
                this.toggleAsset(rId, state);
            }
        });

        // Tool bar events
        this.toolbar.attachEvent('onStateChange', (id, state) => {
            switch (id) {
                case 'points': {
                    this.assetData.forEach((asset) => {
                        const zoom = this.manager.app.layout.avl2Tab.mapPanel.map.map.getZoom();
                        asset?.polyline.showMarkers(state, zoom);
                    });
                    break;
                }
            }
        });
    }

    /** Toggle an asset tracks/markers on and off.
     *
     * @param assetId The id of the asset ( also the asset grid row id )
     * @param state true = show and false = hide
     */
    private toggleAsset(assetId: string, state: boolean): void {
        const asset = this.assetData.get(assetId);
        if (asset) {
            asset.polyline?.polyline?.setVisible(state);
            asset.polyline?.markers.forEach((marker) => {
                if (state == true && this.toolbar.getItemState('points') == true) {
                    marker.setVisible(true);
                } else {
                    marker.setVisible(false);
                }
            });
        }
    }

    /** Fired when user selects an asset from the grid.
     *
     * @param assetid
     */
    public selectAssetFromGrid(assetid: string): void {
        this.tabbar.cells('data_points').showView(assetid);
        const asset = this.assetData.get(assetid);
        if (asset != undefined) {
            asset?.drawSpeedGraph();
        } else {
            this.speedgraph.clearChart();
        }
    }

    /** Fired when the user has edited the grid.
     * In this case it's when the user as picked a colour.
     *
     * @param stage 2 = Editor closed
     * @param assetId  The asset id
     * @param newValue  The colour of the trackline
     */
    private assetGridEdit(stage: number, assetId: string, newValue: string): void {
        if (stage == 2) {
            const asset = this.assetData.get(assetId);
            if (asset) {
                asset.polyline.setColour(newValue);
            }
        }
    }

    /** Makes the request to the server for track data.
     *
     */
    private getTracks(): void {
        this.removeAllTracks();

        this.assetData.forEach((value, _key) => {
            this.tabbar.cells('data_points').unloadView(value.id);
        });

        this.assetData.clear();

        const startTime = this.paramForm.getItemValue(TimeSelect.START);
        const endTime = this.paramForm.getItemValue(TimeSelect.END);
        this.userDefinedStopTimeSeconds = parseInt(this.paramForm.getItemValue('stop_time') as string);

        const ids = this.assetGrid.getAllRowIds(',').split(',');

        this.layout.cells('b').progressOn();

        const bounds = new google.maps.LatLngBounds();
        this.layout.cells('b').showStatusBar();

        const first = ids[0];

        ids.forEach((id) => {
            this.loadAllTelemetryPages(id, startTime.getTime(), endTime.getTime(), 5000, (pageData) => {
                console.log('Received page of size', pageData.length);
                const data = this.loadTelemetry(pageData[0].assetId, pageData);

                // Update UI or process the page data here
            })
                .then(() => {
                    console.log('All data loaded');
                    const asset = this.assetData.get(first);
                    asset?.drawSpeedGraph();
                })
                .catch((err) => {
                    console.error('Error loading telemetry:', err);
                });

            /*            
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            let totalRows = 0;

            const fetcher = new PaginatedFetcher<DeviceTelemetryDTO>('/telemetry/page/' + id + '?start=' + startTime.getTime() + '&end=' + endTime.getTime());
            this.layout.cells('b').showStatusBar();
            fetcher.fetchAllPages((pageData, page, totalPages) => {
                if (page + 1 == totalPages) {
                    //this.layout.cells('b').hideStatusBar();
                }

                const data = this.loadTelemetry(pageData[0].assetId, pageData);

                this.statusbar.setText('Loading Data ' + (page + 1) + '/' + totalPages);

                if (data) {
                    console.log(data.polyline.bounds);
                    bounds.union(data.polyline.bounds);
                    totalRows += data.speeddata.getNumberOfRows();
                    this.manager.app.layout.avl2Tab.mapPanel.map.map.fitBounds(bounds);
                }
            });
            */
        });

        this.layout.cells('b').progressOff();
    }

    /** Fired when the user changes the form
     *
     * @param name
     * @param value
     * @param state
     */
    public formChange(name: string, value: string | number, _state: boolean) {
        if (name == TimeSelect.SELECT) {
            this.populateTime(value);
        }
    }

    /** Set the time range on the form.
     *
     * @param period
     */
    private populateTime(period: string | number): void {
        const select = new TimeSelect(this.paramForm);
        select.selectRange(period as string);
    }

    /** Loads the telemetry from the server.
     * Creates the AssetData and adds them to the cache.
     *
     * @param id
     * @param telemetry
     * @returns
     */
    private loadTelemetry(id: string | undefined, telemetry: DeviceTelemetryDTO[] | undefined): AssetData | null {
        if (id == undefined || telemetry == undefined) {
            return null;
        }

        //var data = new AssetData(this, id);
        let data = this.assetData.get(id);

        if (data == null) {
            this.tabbar.cells('data_points').showView(id);
            data = new AssetData(this, id, this.userDefinedStopTimeSeconds);
        }
        console.time('myTask');
        data.loadTelemetry(telemetry);
        console.timeEnd('myTask');

        this.assetData.set(id, data);

        return data;
    }

    /** Clean up the map by removing all tracks and markers
     *
     */
    private removeAllTracks() {
        this.speedgraph?.clearChart();

        this.assetData.forEach((value, _key) => {
            value.clearStopMarkers();
            if (value.polyline) {
                value.polyline.polyline.setMap(null);
                value.polyline.markers.forEach((marker) => {
                    marker.setMap(null);
                });
            }
        });
    }

    resize() {
        this.layout?.setSizes();
        console.log(this.manager.app.saveUserSettings());
    }

    setTime(period: string) {
        this.populateTime(period);
    }

    async fetchTelemetryPage(assetId: string, start: string, end: string, lastDateOccurred: string | null, pageSize: number): Promise<TelemetryResponse> {
        const params = new URLSearchParams({
            assetId,
            start,
            end,
            pageSize: pageSize.toString(),
        });

        if (lastDateOccurred) {
            params.append('lastDateOccurred', lastDateOccurred);
        }

        const url = this.assetService.url + '/api/' + this.assetService.orgid + '/telemetry/offset';

        const response = await fetch(url + `?${params.toString()}`, {
            headers: {
                method: 'GET',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + this.assetService.jwt,
            },
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch telemetry data: ${response.statusText}`);
        }
        return response.json();
    }

    async loadAllTelemetryPages(assetId: string, start: string, end: string, pageSize: number, onPage: (pageData: DeviceTelemetryDTO[]) => void): Promise<void> {
        let lastCursor: string | null = null;
        while (true) {
            const page = await this.fetchTelemetryPage(assetId, start, end, lastCursor, pageSize);
            if (page.data.length === 0) {
                this.statusbar.setText('Load Complete');
                break; // no more data
            }

            // Call callback with current page data
            onPage(page.data);

            if (lastCursor == null) {
                this.statusbar.setText('Loading Data');
            } else {
                this.statusbar.setText('Loaded ' + page.data.length);
            }
            // Update cursor for next page
            lastCursor = page.lastDateOccurred;
            if (!lastCursor) {
                this.statusbar.setText('Load Complete');
                break; // no more pages
            }
        }
    }
}
